import getApplicationSettings from 'owa-application-settings/lib/selectors/getApplicationSettings';

/**
 * This list of safe protocols is used to prevent XSS attacks by only allowing
 * certain protocols in href and src attributes.
 *
 * This list changes so infrequently (only once in few years) that it's safe to
 * hardcode it, must match the list in
 * https://o365exchange.visualstudio.com/O365%20Core/_git/Substrate?path=/sources/dev/CTS/src/Server/TextConverters/Html/UrlSchemaChecker.cs
 */
const defaultSafeProtocols = [
    'about',
    'blob',
    'callto',
    'cid',
    'codeflow',
    'conf',
    'file',
    'ftp',
    'gopher',
    'groove',
    'http',
    'https',
    'im',
    'ipp',
    'mailto',
    'meet',
    'mhtml',
    'mms',
    'msdaipp',
    'ms-excel',
    'ms-powerpoint',
    'ms-word',
    'ms-outlook',
    'news',
    'notes',
    'onenote',
    'sip',
    'tel',
    'wais',
] as const;

let cachedAllowedUriRegExp: RegExp | undefined;

/* @internal — exposed for test isolation only */
export function resetAllowedUriRegExpCache(): void {
    cachedAllowedUriRegExp = undefined;
}

/**
 * Builds (and caches) the ALLOWED_URI_REGEXP from the default safe-protocol
 * list plus any server-configured schemas.
 */
export function getAllowedUriRegExp(): RegExp {
    if (!cachedAllowedUriRegExp) {
        const applicationSettings = getApplicationSettings('UrlValidationSettings');
        let safeProtocols = defaultSafeProtocols.join('|');
        if (applicationSettings.whiteListedSchemas.length > 0) {
            safeProtocols += `|${applicationSettings.whiteListedSchemas.join('|')}`;
        }
        cachedAllowedUriRegExp = new RegExp(
            `^(?:(?:${safeProtocols}):|[^a-z]|[a-z+.\\-]+(?:[^a-z+.\\-:]|$))`,
            'i'
        );
    }
    return cachedAllowedUriRegExp;
}

/**
 * URI-carrying attributes to scrub. Each entry carries a CSS selector
 * (namespaced colons must be escaped), the DOM attribute name used for
 * getAttribute/removeAttribute, and whether an inline `data:` URI is allowed
 * on a media element for that attribute (mirrors DOMPurify's data-URI carve-out).
 */
const URI_ATTRIBUTES: { selector: string; name: string; allowDataUri?: boolean }[] = [
    { selector: '[href]', name: 'href', allowDataUri: true },
    { selector: '[src]', name: 'src', allowDataUri: true },
    { selector: '[action]', name: 'action' },
    { selector: '[formaction]', name: 'formaction' },
    { selector: '[xlink\\:href]', name: 'xlink:href', allowDataUri: true },
];

/**
 * Media tags on which an inline `data:` URI is permitted, mirroring DOMPurify's
 * DEFAULT_DATA_URI_TAGS. The `data` scheme is intentionally absent from the
 * safe-protocol list (allowing it everywhere is an XSS risk), but inline base64
 * media — e.g. signature/email logos using `data:image/...;base64,...` — must be
 * preserved on these elements, exactly as DOMPurify does.
 */
const DATA_URI_TAGS = new Set(['audio', 'video', 'img', 'source', 'image', 'track']);

/**
 * Whitespace and control characters that the URL parser strips from an attribute
 * value before resolving its scheme. These must be removed before the protocol
 * check or ` javascript:` / `java&#10;script:` would pass the regex while the
 * browser still navigates to `javascript:`. Mirrors DOMPurify's ATTR_WHITESPACE.
 */
const ATTR_WHITESPACE = /[\u0000-\u0020\u00a0\u1680\u180e\u2000-\u2029\u205f\u3000]/g;

/**
 * Post-sanitization URI scrubber for the native Sanitizer API path.
 *
 * The native HTML Sanitizer API has no equivalent of DOMPurify's ALLOWED_URI_REGEXP.
 * This function walks URI-carrying attributes and removes any whose value is neither
 * an allowed protocol nor an allowed inline `data:` media URI.
 *
 * @param root The sanitized DOM root to scrub URIs in.
 * @param skipUriFiltering When true (ALLOW_UNKNOWN_PROTOCOLS), skip URI filtering entirely.
 */
export function postProcessUris(
    root: Element | DocumentFragment,
    skipUriFiltering?: boolean
): void {
    if (skipUriFiltering) {
        return;
    }

    const uriRegex = getAllowedUriRegExp();

    for (const { selector, name, allowDataUri } of URI_ATTRIBUTES) {
        const elements = root.querySelectorAll(selector);
        elements.forEach(el => {
            const value = el.getAttribute(name);
            if (!value) {
                return;
            }

            const isSafe =
                uriRegex.test(value.replace(ATTR_WHITESPACE, '')) ||
                (allowDataUri && value.indexOf('data:') === 0 && DATA_URI_TAGS.has(el.localName));

            if (!isSafe) {
                el.removeAttribute(name);
            }
        });
    }
}
